import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";

const MINIMUM_WIDE_ARTWORK_HEIGHT = 200;
const SLIDE_INTERVAL_MS = 3000;

function useHeroArtworkHeight(heroRef: RefObject<HTMLElement>) {
  useLayoutEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;
    const actions = hero.querySelector<HTMLElement>(".hero-actions");
    const shell = hero.closest("main")?.parentElement;
    if (!actions || !shell) return;

    function sizeArtwork() {
      if (!hero || !actions) return;
      const viewportHeight = window.innerHeight;
      const heroTop = hero.getBoundingClientRect().top + window.scrollY;
      const navigation = document.querySelector('nav[aria-label="Primary"]')?.getBoundingClientRect();
      const bottomSpace = navigation?.height ? viewportHeight - navigation.top : 0;
      const availableHeight = Math.max(0, viewportHeight - heroTop - bottomSpace);
      const heightWithActions = availableHeight - actions.getBoundingClientRect().height;
      // Desktop actions overlay the artwork; reserve only the search card's clearance below it.
      const desktopHeight = availableHeight - Number.parseFloat(getComputedStyle(hero).paddingBottom);
      const artworkHeight = window.innerWidth >= 1280
        ? Math.max(MINIMUM_WIDE_ARTWORK_HEIGHT, desktopHeight)
        : window.innerWidth >= 640 && heightWithActions >= MINIMUM_WIDE_ARTWORK_HEIGHT
          ? heightWithActions : availableHeight;
      hero.style.setProperty("--hero-art-max-height", `${artworkHeight}px`);
    }

    const observer = new ResizeObserver(sizeArtwork);
    observer.observe(shell);
    observer.observe(actions);
    window.addEventListener("resize", sizeArtwork);
    sizeArtwork();
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", sizeArtwork);
    };
  }, [heroRef]);
}

function OpportunitySlide() {
  return (
    <>
      <img src="/KontrataPH-Icon.svg" alt="" aria-hidden="true" className="hero-campaign-watermark absolute" />
      <div className="hero-campaign-copy relative z-10">
        <div className="hero-campaign-brand flex items-center gap-3" aria-hidden="true">
          <img src="/KontrataPH-Icon.svg" alt="" width="48" height="48" />
          <span>Kontrata<span className="text-[#df0000]">PH</span></span>
        </div>
        <span className="hero-campaign-accent block" aria-hidden="true" />
        <h1 id="home-hero-heading">More Opportunities<br /><span>for Every Filipino.</span></h1>
        <p>Discover jobs, services, projects,<br className="hidden sm:block" /> and business opportunities on KontrataPH.</p>
      </div>
      <div className="hero-campaign-portrait relative">
        <img src="/hero/approved/Banner_2.png" alt="" aria-hidden="true" width="4287" height="4287"
          className="hero-campaign-ribbon-right absolute" />
        <img src="/hero/approved/KontrataPHMannyPhoto.png" alt="Manny Pacquiao wearing a KontrataPH shirt" width="1372" height="1775"
          className="hero-campaign-person absolute" />
        <img src="/hero/approved/Banner_1.png" alt="" aria-hidden="true" width="4287" height="4287"
          className="hero-campaign-ribbon-left absolute" />
      </div>
    </>
  );
}

function FutureSlide() {
  return (
    <div className="hero-future-artwork">
      <img src="/hero/approved/Carousel_2_BG.jpg" alt="" aria-hidden="true" width="1920" height="1080" className="hero-future-background" />
      <div className="hero-future-copy">
        <p className="hero-future-kicker" lang="tl">KASAMA SA IYONG PAG-ASENSO</p>
        <h2 lang="tl">Kung may Kontrata at Trabaho.<br /><span>Aasenso ang bawat Pilipino.</span></h2>
        <p className="hero-future-description">Find jobseekers. Discover opportunities.<br />Build a better future with KontrataPH.</p>
      </div>
      <img src="/hero/approved/Manny_Banner.png" alt="Manny Pacquiao wearing a KontrataPH shirt with his arms crossed" width="420" height="594" className="hero-future-person" />
      <div className="hero-future-caption">
        <strong>Sec. Manny Pacquiao</strong>
        <span>Secretary and Lead Convenor</span>
        <span>National Anti-Poverty Commission</span>
      </div>
    </div>
  );
}

function useHeroRotation() {
  const [activeSlide, setActiveSlide] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (reducedMotion || hovered || focused) return;
    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") setActiveSlide((slide) => (slide + 1) % 2);
    }, SLIDE_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [activeSlide, reducedMotion, hovered, focused]);

  return { activeSlide, setActiveSlide, setHovered, setFocused };
}

export function HomeHero({ children }: { children: ReactNode }) {
  const heroRef = useRef<HTMLElement>(null);
  useHeroArtworkHeight(heroRef);
  const { activeSlide, setActiveSlide, setHovered, setFocused } = useHeroRotation();

  return (
    <section ref={heroRef} className="landing-hero bg-white" aria-label="Featured opportunities" aria-roledescription="carousel" data-slide={activeSlide + 1}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setFocused(true)} onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false); }}>
      <div className="hero-approved-artwork relative isolate mx-auto overflow-hidden" aria-live="off">
        <div className={`hero-slide ${activeSlide === 0 ? "is-active" : ""}`} role="group" aria-roledescription="slide" aria-label="1 of 2" aria-hidden={activeSlide !== 0}><OpportunitySlide /></div>
        <div className={`hero-slide ${activeSlide === 1 ? "is-active" : ""}`} role="group" aria-roledescription="slide" aria-label="2 of 2" aria-hidden={activeSlide !== 1}><FutureSlide /></div>
        <div className="hero-carousel-controls" role="group" aria-label="Banner controls">
          {[0, 1].map((slide) => <button key={slide} type="button" onClick={() => setActiveSlide(slide)} aria-label={`Show banner ${slide + 1}`} aria-current={activeSlide === slide ? "true" : undefined}><span /></button>)}
        </div>
      </div>
      <div className="hero-actions mx-auto px-4 py-5 sm:px-6 sm:py-6">{children}</div>
    </section>
  );
}
